import React, { memo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box } from '@mui/material';
import { normalizeHtml } from '../../../../utils/normalizeHtml';

interface MemoizedQuillEditorProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
    readOnly?: boolean;
    placeholder?: string;
}

// Configuração do editor (fora do componente para não recriar a cada render)
const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link'],
        ['clean']
    ],
};

const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
];

export const MemoizedQuillEditor = memo<MemoizedQuillEditorProps>(({
    value,
    onChange,
    onBlur,
    readOnly = false,
    placeholder
}) => {
    const handleChange = useCallback((content: string) => {
        onChange(content);
    }, [onChange]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        if (onBlur) {
            // Normaliza apenas quando o blur acontece no container do Quill
            if (value && !e.currentTarget.contains(e.relatedTarget as Node)) {
                const normalized = normalizeHtml(value);
                if (normalized !== value) {
                    onChange(normalized);
                }
            }
            onBlur(e);
        }
    }, [value, onChange, onBlur]);

    return (
        <Box onBlur={handleBlur}>
            <ReactQuill
                value={value || ''}
                onChange={handleChange}
                theme="snow"
                style={{ background: '#fff', borderRadius: 8 }}
                readOnly={readOnly}
                modules={quillModules}
                formats={quillFormats}
                placeholder={placeholder}
            />
        </Box>
    );
}, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renders desnecessários
    return (
        prevProps.value === nextProps.value &&
        prevProps.readOnly === nextProps.readOnly &&
        prevProps.placeholder === nextProps.placeholder
    );
});

MemoizedQuillEditor.displayName = 'MemoizedQuillEditor';

